import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';

// Material UI v4
import { 
  Box, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, 
  Typography, Button, TableContainer, TablePagination, TextField, 
  useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions, 
  Fab, IconButton, Snackbar, List, ListItem, ListItemText 
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import Visibility from '@material-ui/icons/Visibility';

// Resources
import { CSVLink } from 'react-csv';
import { 
  requestUsersByOfficeId, requestEditUsersByEmail,
  requestAccountsUsersByEmail, requestDeleteUserByEmail 
} from '../actions/actions';
import Utils from '../resources/Utils';
import CircleLoading from './CircleLoading';

// ----------------------------------------------------------------------
// Ícono SVG nativo para el botón de filtros en móvil
// ----------------------------------------------------------------------
const FilterIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
    <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1, 1), 
    [theme.breakpoints.up('sm')]: { padding: theme.spacing(1, 2), },
    [theme.breakpoints.up('md')]: { padding: theme.spacing(1, 3), }
  },
  pageTitle: {
    fontWeight: 600,
    marginTop: 0, 
    marginBottom: theme.spacing(2), 
    color: '#333',
    [theme.breakpoints.down('xs')]: { fontSize: '1.25rem', textAlign: 'center' }
  },
  filterPaper: {
    padding: '16px 20px', 
    marginBottom: theme.spacing(2),
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)',
  },
  actionsContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px', 
    flexWrap: 'nowrap', 
    justifyContent: 'flex-end', 
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch', 
    }
  },
  csvLinkWrapper: {
    textDecoration: 'none',
    [theme.breakpoints.down('sm')]: { width: '100%', display: 'block' }
  },
  tableContainer: {
    borderRadius: '8px 8px 0 0',
    overflowX: 'auto', 
    WebkitOverflowScrolling: 'touch', 
    [theme.breakpoints.down('sm')]: { maxHeight: 'calc(100vh - 250px)' },
    [theme.breakpoints.up('md')]: { maxHeight: 'calc(100vh - 280px)', minHeight: '300px' }
  },
  headerTableCell: {
    fontWeight: 600,
    color: '#333', 
    whiteSpace: 'nowrap',
    padding: '12px 16px', 
  },
  smallCell: {
    whiteSpace: 'nowrap',
    padding: '6px 16px', 
  },
  stickyLeft: {
    position: 'sticky',
    left: 0,
    backgroundColor: '#ffffff', 
    zIndex: 1,
    borderRight: '1px solid #e0e0e0',
  },
  stickyHeaderLeft: {
    position: 'sticky',
    left: 0,
    zIndex: 3, 
    borderRight: '1px solid rgba(0,0,0,0.1)',
  },
  emailWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px', 
    whiteSpace: 'nowrap',
  },
  tableActionButton: {
    textTransform: 'none',
    minWidth: 'auto', 
    marginRight: theme.spacing(1),
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      marginRight: 0,
      marginBottom: theme.spacing(1)
    }
  },
  paginationWrapper: {
    backgroundColor: '#ffffff', 
    borderTop: '1px solid #e0e0e0',
    [theme.breakpoints.down('xs')]: {
      '& .MuiTablePagination-toolbar': {
        minHeight: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', alignItems: 'center'
      },
      '& .MuiTablePagination-spacer': { display: 'none' },
      '& .MuiTablePagination-caption': { margin: '4px 0' },
      '& .MuiTablePagination-selectRoot': { margin: '4px 0' },
      '& .MuiTablePagination-actions': { marginLeft: 0, marginTop: '8px' }
    }
  },
  fab: {
    position: 'fixed',
    bottom: theme.spacing(10), 
    right: theme.spacing(3),
    zIndex: 1000,
    boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
  },
  dialogTitleWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2, 3),
    borderBottom: '1px solid #e0e0e0'
  }
}));

const UsersTable = () => {
  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); 

  const { init } = useSelector((state) => state);
  const usersReq = init.users;
  const accountsData = init.accountsUsers?.accounts || [];
  const configData = init.config?.data || {};

  const [filters, setFilters] = useState({
    pageNumber: 1,
    pageSize: 5,
    searchByReference: 'default',
    initialDate: '',
    finalDate: ''
  });

  // Estado para la interfaz del Input del correo (Necesario para el Debounce)
  const [emailSearchUI, setEmailSearchUI] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [openFiltersDialog, setOpenFiltersDialog] = useState(false); 
  const [openAccountsDialog, setOpenAccountsDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUserEmail, setSelectedUserEmail] = useState('');

  const [snackbars, setSnackbars] = useState({
    updateError: false,
    deleteOk: false,
    deleteError: false
  });

  // Referencias para evitar el problema de los Snackbars al presionar F5
  const isFirstMountEdit = useRef(true);
  const isFirstMountDelete = useRef(true);

  const isWarning = usersReq?.status === 'WARNING';
  const rawData = !isWarning && usersReq?.data?.data ? usersReq.data.data : [];
  const totalPages = !isWarning && usersReq?.data?.totalPage ? usersReq.data.totalPage : 0;
  const uiPage = filters.pageNumber - 1; 
  const backgroundColor = configData.appBarColor || theme.palette.primary.main;
  const msgs = Utils.getMessages();
  const date = new Date().toISOString().substring(0, 10);

  // ----------------------------------------------------------------------
  // Debounce Effect para proteger el Backend al escribir en la búsqueda
  // ----------------------------------------------------------------------
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const apiValue = Utils.isEmpty(emailSearchUI) ? 'default' : emailSearchUI;
      
      if (filters.searchByReference !== apiValue) {
        updateFiltersAndFetch({ ...filters, searchByReference: apiValue, pageNumber: 1 });
      }
    }, 500); 

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSearchUI]);

  useEffect(() => {
    setIsLoading(true);
    dispatch(requestUsersByOfficeId(filters));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (usersReq) setIsLoading(false);
  }, [usersReq]);

  // Efecto para controlar el mensaje de Edición de estatus
  useEffect(() => {
    if (isFirstMountEdit.current) {
      isFirstMountEdit.current = false;
      return;
    }
    if (init.errorEditStatusUser?.status === "ERROR") {
      setSnackbars(prev => ({ ...prev, updateError: true }));
    }
  }, [init.errorEditStatusUser]);

  // Efecto para controlar el mensaje de Eliminación de usuario (Blindado)
  useEffect(() => {
    if (isFirstMountDelete.current) {
      isFirstMountDelete.current = false;
      return;
    }
    
    if (init.deleteUser?.status === 'OK') {
      setSnackbars(prev => ({ ...prev, deleteOk: true }));
      // Recargar usando los filtros actuales del estado
      dispatch(requestUsersByOfficeId(filters));
    } else if (init.deleteUser?.status === 'ERROR') {
      setSnackbars(prev => ({ ...prev, deleteError: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [init.deleteUser]); 

  const updateFiltersAndFetch = (newFilters) => {
    setIsLoading(true);
    setFilters(newFilters);
    dispatch(requestUsersByOfficeId(newFilters));
  };

  const handleChangePage = (event, newPage) => {
    updateFiltersAndFetch({ ...filters, pageNumber: newPage + 1 });
  };

  const handleChangeRowsPerPage = (event) => {
    updateFiltersAndFetch({ ...filters, pageSize: parseInt(event.target.value, 10), pageNumber: 1 });
  };

  const handleChangeDate = (field) => (event) => {
    const value = event.target.value;
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    if (newFilters.initialDate && newFilters.finalDate) {
      setIsLoading(true);
      dispatch(requestUsersByOfficeId(newFilters));
    }
  };

  const handleTexfieldsClear = () => {
    setEmailSearchUI(''); 
    updateFiltersAndFetch({
      ...filters,
      searchByReference: 'default',
      initialDate: '',
      finalDate: '',
      pageNumber: 1
    });
  };

  const handleUpdateStatusUser = (email, status) => {
    dispatch(requestEditUsersByEmail({ email, status }));
  };

  const handleShowAccountsByUser = (email) => {
    setSelectedUserEmail(email);
    setOpenAccountsDialog(true);
    dispatch(requestAccountsUsersByEmail({ email }));
  };

  const handleShowUserToDelete = (email) => {
    setSelectedUserEmail(email);
    setOpenDeleteDialog(true);
  };

  const confirmDeleteUser = () => {
    setOpenDeleteDialog(false);
    dispatch(requestDeleteUserByEmail({ email: selectedUserEmail }));
  };

  const closeSnackbar = (key) => {
    setSnackbars(prev => ({ ...prev, [key]: false }));
  };

  const renderFiltersForm = () => (
    <Grid container spacing={2} alignItems="center">
      <Grid item xs={12} sm={12} md={4} lg={3}>
        <TextField 
          fullWidth 
          variant="outlined" 
          size="small" 
          value={emailSearchUI} 
          InputLabelProps={{ shrink: true }} 
          type="email" 
          label={msgs.usersHeaders.email} 
          onChange={(e) => setEmailSearchUI(e.target.value)} 
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField fullWidth variant="outlined" size="small" type="date" value={filters.initialDate} InputLabelProps={{ shrink: true }} inputProps={{ max: date }} label={msgs.paymentsMessages.initialDate} onChange={handleChangeDate('initialDate')} />
      </Grid>
      <Grid item xs={12} sm={6} md={4} lg={3}>
        <TextField fullWidth variant="outlined" size="small" type="date" value={filters.finalDate} InputLabelProps={{ shrink: true }} inputProps={{ max: date }} label={msgs.paymentsMessages.finalDate} onChange={handleChangeDate('finalDate')} />
      </Grid>

      <Grid item xs={12} md={12} lg={3}>
        <div className={classes.actionsContainer}>
          <Button variant="outlined" color="primary" onClick={handleTexfieldsClear} fullWidth={isMobile}>
            {msgs.paymentsMessages.buttonClear}
          </Button>
          <CSVLink data={rawData} filename={msgs.usersMessages.title} className={classes.csvLinkWrapper}>
            <Button variant="contained" color="primary" fullWidth={isMobile}>
              {msgs.General.exportCSV}
            </Button>
          </CSVLink>
        </div>
      </Grid>
    </Grid>
  );

  const renderPagination = (isTop) => (
    <TablePagination
      className={classes.paginationWrapper}
      style={isTop ? { borderTop: 'none', borderBottom: '1px solid #e0e0e0', borderRadius: '8px 8px 0 0' } : {}}
      labelRowsPerPage={isMobile ? "Filas" : <Typography variant="body2">{msgs.pagination.rowsPerPages}</Typography>}
      labelDisplayedRows={({ from, to, count }) => <Typography variant="body2">{from} - {to} {msgs.pagination.of} {count}</Typography>}
      rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25, 50, 100]} 
      component="div"
      count={totalPages}
      rowsPerPage={filters.pageSize}
      page={uiPage}
      backIconButtonText={msgs.pagination.previousPage}
      nextIconButtonText={msgs.pagination.nextPage}
      onChangePage={handleChangePage}
      onChangeRowsPerPage={handleChangeRowsPerPage}
    />
  );

  return (
    <Box className={classes.root}>
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbars.updateError} autoHideDuration={5000} onClose={() => closeSnackbar('updateError')} message={msgs.usersMessages.errorUpdate} action={<IconButton size="small" color="inherit" onClick={() => closeSnackbar('updateError')}><CloseIcon fontSize="small" /></IconButton>} />
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbars.deleteOk} autoHideDuration={5000} onClose={() => closeSnackbar('deleteOk')} message={msgs.usersMessages.deleteUserOk} action={<IconButton size="small" color="inherit" onClick={() => closeSnackbar('deleteOk')}><CloseIcon fontSize="small" /></IconButton>} />
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={snackbars.deleteError} autoHideDuration={5000} onClose={() => closeSnackbar('deleteError')} message={msgs.usersMessages.deleteUserError} action={<IconButton size="small" color="inherit" onClick={() => closeSnackbar('deleteError')}><CloseIcon fontSize="small" /></IconButton>} />

      <Typography variant="h5" className={classes.pageTitle}>{Utils.getMessages().usersMessages.title}</Typography>

      {!isMobile && (
        <Paper elevation={0} className={classes.filterPaper}>
          {renderFiltersForm()}
        </Paper>
      )}

      {isMobile && (
        <>
          <Fab className={classes.fab} color="primary" onClick={() => setOpenFiltersDialog(true)}>
            <FilterIcon />
          </Fab>
          <Dialog open={openFiltersDialog} onClose={() => setOpenFiltersDialog(false)} fullWidth maxWidth="sm">
            <DialogTitle disableTypography style={{ paddingBottom: '8px' }}>
              <Typography variant="h6" style={{ fontWeight: 600, color: '#333' }}>Filtros de Búsqueda</Typography>
            </DialogTitle>
            <DialogContent dividers>{renderFiltersForm()}</DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenFiltersDialog(false)} color="primary" style={{ fontWeight: 'bold' }}>Cerrar</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={5}>
          <CircleLoading mensajeCargando="Cargando datos" />
        </Box>
      ) : isWarning || rawData.length === 0 ? (
        <Paper elevation={1} style={{ padding: '40px', textAlign: 'center' }}>
          <Typography variant='h6' color="textSecondary">{msgs.paymentsMessages.notData}</Typography>
        </Paper>
      ) : (
        <Paper elevation={1}>
          
          {/* PAGINACIÓN SUPERIOR (CELULAR) */}
          {isMobile && renderPagination(true)}

          {isMobile ? (
            <Box p={2} style={{ maxHeight: 'calc(100vh - 250px)', overflowY: 'auto' }}>
              {rawData.map((user, idx) => (
                <Paper key={idx} elevation={2} style={{ padding: '16px', marginBottom: '16px', borderRadius: '12px', borderLeft: `4px solid ${user.status === 1 ? '#4caf50' : '#f44336'}` }}>
                  
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Box display="flex" alignItems="center" style={{ overflow: 'hidden' }}>
                      <IconButton size="small" color="primary" style={{ padding: '4px', marginRight: '8px' }} onClick={() => handleShowAccountsByUser(user.email)}>
                        <Visibility fontSize="small" />
                      </IconButton>
                      <Typography variant="subtitle2" style={{ fontWeight: 600, wordBreak: 'break-all', lineHeight: 1.2 }}>
                        {user.email}
                      </Typography>
                    </Box>
                  </Box>

                  <Box mb={2} pl={4}>
                    <Typography variant="caption" color="textSecondary" display="block">
                      <strong>Registro:</strong> {user.registerDate}
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block">
                      <strong>Última sesión:</strong> {user.ultimatedlogin}
                    </Typography>
                  </Box>

                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Button fullWidth size="small" variant="contained" style={{ textTransform: 'none' }} color={user.status === 1 ? "primary" : "secondary"} onClick={() => handleUpdateStatusUser(user.email, user.status === 1 ? 2 : 1)}>
                        {user.status === 1 ? msgs.usersMessages.buttonBaja : msgs.usersMessages.buttonAlta}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button fullWidth size="small" variant="outlined" style={{ textTransform: 'none' }} color="primary" onClick={() => handleShowUserToDelete(user.email)}>
                        {msgs.usersMessages.deleteUser}
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              ))}
            </Box>
          ) : (
            <TableContainer className={classes.tableContainer}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      msgs.usersHeaders.email, msgs.usersHeaders.status, msgs.usersHeaders.regsiterDate, 
                      msgs.usersHeaders.lastSession, 'Acciones'
                    ].map((header, idx) => (
                      <TableCell 
                        key={idx} 
                        className={idx === 0 ? classes.stickyHeaderLeft : ''}
                        style={{ backgroundColor, padding: '12px 16px' }} 
                        colSpan={header === 'Acciones' ? 2 : 1}
                      >
                        <Typography variant="body2" className={classes.headerTableCell}>{header}</Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rawData.map((user, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell className={`${classes.smallCell} ${classes.stickyLeft}`}>
                        <div className={classes.emailWrapper}>
                          <IconButton size="small" color="primary" title={msgs.usersMessages.showAccounts} onClick={() => handleShowAccountsByUser(user.email)}>
                            <Visibility fontSize="small" />
                          </IconButton>
                          <Typography variant="body2" color="textPrimary">{user.email}</Typography>
                        </div>
                      </TableCell>
                      <TableCell className={classes.smallCell}>
                        <Typography variant="body2" style={{ fontWeight: 500, color: user.status === 1 ? '#4caf50' : '#f44336' }}>
                          {user.status === 1 ? msgs.usersMessages.active : msgs.usersMessages.notActive}
                        </Typography>
                      </TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{user.registerDate}</Typography></TableCell>
                      <TableCell className={classes.smallCell}><Typography variant="body2">{user.ultimatedlogin}</Typography></TableCell>
                      
                      <TableCell className={classes.smallCell} style={{ paddingRight: theme.spacing(1) }}>
                        <Button size="small" variant="contained" className={classes.tableActionButton} color={user.status === 1 ? "primary" : "secondary"} onClick={() => handleUpdateStatusUser(user.email, user.status === 1 ? 2 : 1)}>
                          {user.status === 1 ? msgs.usersMessages.buttonBaja : msgs.usersMessages.buttonAlta}
                        </Button>
                      </TableCell>
                      <TableCell className={classes.smallCell} style={{ paddingLeft: 0 }}>
                        <Button size="small" variant="outlined" className={classes.tableActionButton} color="primary" onClick={() => handleShowUserToDelete(user.email)}>
                          {msgs.usersMessages.deleteUser}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* PAGINACIÓN INFERIOR (ESCRITORIO) */}
          {!isMobile && renderPagination(false)}

        </Paper>
      )}

      {/* DIÁLOGO: CUENTAS POR USUARIO */}
      <Dialog open={openAccountsDialog} onClose={() => setOpenAccountsDialog(false)} fullWidth maxWidth="xs">
        <div className={classes.dialogTitleWrapper}>
          <Typography variant="h6" style={{ fontWeight: 600 }}>{selectedUserEmail}</Typography>
          <IconButton size="small" onClick={() => setOpenAccountsDialog(false)}><CloseIcon /></IconButton>
        </div>
        <DialogContent style={{ padding: 0 }}>
          <Box p={2} bgcolor="#f5f5f5" borderBottom="1px solid #e0e0e0">
            <Typography variant="subtitle2" align="center" color="textSecondary">
              {msgs.logsUsersPaymentsHeaders.accountNumber}
            </Typography>
          </Box>
          {accountsData.length > 0 ? (
            <List style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {accountsData.map((account, idx) => (
                <ListItem key={idx} divider={idx !== accountsData.length - 1}>
                  <ListItemText primary={account.accountNumber} primaryTypographyProps={{ align: 'center', variant: 'body2' }} />
                </ListItem>
              ))}
            </List>
          ) : (
            <Box p={4} textAlign="center">
              <Typography variant="body2" color="textSecondary">{msgs.paymentsMessages.notData}</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* DIÁLOGO: ELIMINAR USUARIO */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} maxWidth="xs">
        <DialogTitle>{msgs.usersMessages.alert}</DialogTitle>
        <DialogContent>
          <Typography align="center" variant="body1">
            {msgs.usersMessages.descriptionAlertDelete1} <br/>
            <strong>{selectedUserEmail}</strong> <br/>
            {msgs.usersMessages.descriptionAlertDelete2} (<b>{msgs.usersMessages.accept}</b>)
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: '16px' }}>
          <Button onClick={() => setOpenDeleteDialog(false)} variant="outlined" color="primary">
            {msgs.usersMessages.cancel}
          </Button>
          <Button onClick={confirmDeleteUser} variant="contained" color="primary">
            {msgs.usersMessages.accept}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default withRouter(UsersTable);